<template>
  <div :class="'custom_input '+width">
    <div
      v-if="label"
      class="label"
    >
      {{ capitalisedLabel }}
    </div>
    <multiselect
      v-model="
        // eslint-disable-next-line vue/no-mutating-props
        modelValue
      "
      :can-clear="canClear"
      :can-deselect="false"
      :close-on-select="true"
      :groups="groups"
      :options="options"
      :placeholder="placeholder"
      :disabled="disabled"
      :searchable="searchable"
      class="custom_select"
      mode="single"
      @clear="updateValue()"
      @select="value => updateValue()"
    >
      <template #caret>
        <svg
          class="caret"
          fill="none"
          height="16"
          viewBox="0 0 16 16"
          width="16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.9631 5.65439L8.32102 10.2965L3.67891 5.65439"
            stroke="#333333"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </template>
      <template #singlelabel="{ value }">
        <div
          :style="value.font ? 'font-family:'+value.font+';' : ''"
          class="multiselect-single-label"
        >
          {{ value.label }}
        </div>
      </template>

      <template #option="{ option }">
        <slot
          v-if="$slots['option']"
          name="option"
          v-bind="{option}"
        />
        <span
          v-else
          :style="option.font ? 'font-family:'+option.font+';' : ''"
        >{{ option.label }}</span>
      </template>
    </multiselect>
    <small
      v-for="(error, index) in validationErrors"
      :key="index"
      class="validation"
    >{{ error }}</small>
  </div>
</template>

<script>
import _ from 'lodash';
import Multiselect from '@vueform/multiselect';

export default {
  components: {
    Multiselect
  },
  props: {
    label: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Please Select'
    },
    modelValue: {
      type: [Array, String, Number],
      default: null,
    },
    options: {
      type: [Array, Object, Function],
      required: true
    },
    width: {
      type: String,
      required: false,
      default: 'full'
    },
    canClear: {
      type: Boolean,
      required: false,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    validationErrors: {
      type: Object,
      required: false,
      default: null,
    },
    groups: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    capitalisedLabel() {
      let words = this.label.split(' ');
      _.each(words, (word, index) => {
        words[index] = _.capitalize(word);
      });
      return words.join(' ');
    }
  },
  methods: {
    updateValue() {
      this.$nextTick(() => {
        this.$emit('update:modelValue', this.modelValue);
      })
    },
  }
};
</script>

<style scoped>
.custom_input {
  margin-bottom: 1.5rem;
  color: #4E4E54;
}

.full {
  width: 100%;
}

.two-thirds {
  width: calc(66.66% - .5rem);
}

.half {
  width: calc(50% - .5rem);
}

.third {
  width: calc(33.33% - .5rem);
}

.quarter {
  width: calc(25% - .5rem);
}

.caret {
  margin-right: 24.5px;
}

.label {
  margin-bottom: .25rem;
  font-size: 0.75rem;
  line-height: 1.3;
  letter-spacing: -0.1px;
  color: #909096;
}

.custom_select {
  --ms-font-size: 14px;
  --ms-line-height: 1.375;
  --ms-bg: #FFFFFF;
  --ms-bg-disabled: #E9ECEF;
  --ms-border-color: #E8E5E2;
  --ms-border-width: 1px;
  --ms-radius: .75rem;
  --ms-py: 12.5px;
  --ms-px: 24.5px;
  --ms-ring-width: 0px;
  --ms-ring-color: #10B98130;
  --ms-placeholder-color: #9CA3AF;
  --ms-max-height: 10rem;

  --ms-spinner-color: #10B981;
  --ms-caret-color: #999999;
  --ms-clear-color: #999999;
  --ms-clear-color-hover: #000000;

  --ms-tag-font-size: 14px;
  --ms-tag-line-height: 1.25rem;
  --ms-tag-font-weight: 600;
  --ms-tag-bg: #37B2A8;
  --ms-tag-bg-disabled: #9CA3AF;
  --ms-tag-color: #FFFFFF;
  --ms-tag-color-disabled: #FFFFFF;
  --ms-tag-radius: 4px;
  --ms-tag-py: 0.125rem;
  --ms-tag-px: 0.5rem;
  --ms-tag-my: 0.25rem;
  --ms-tag-mx: 0.25rem;

  --ms-tag-remove-radius: 4px;
  --ms-tag-remove-py: 0.25rem;
  --ms-tag-remove-px: 0.25rem;
  --ms-tag-remove-my: 0rem;
  --ms-tag-remove-mx: 0.125rem;

  --ms-dropdown-bg: #FFFFFF;
  --ms-dropdown-border-color: #D1D5DB;
  --ms-dropdown-border-width: 1px;
  --ms-dropdown-radius: 4px;

  --ms-option-font-size: 14px;
  --ms-option-line-height: 1.375;
  --ms-option-bg-pointed: #FFFFFF;
  --ms-option-bg-selected: #37B2A8;
  --ms-option-bg-disabled: #FFFFFF;
  --ms-option-bg-selected-pointed: #37B2A8;
  --ms-option-bg-selected-disabled: #FFFFFF;
  --ms-option-color-pointed: #1F2937;
  --ms-option-color-selected: #FFFFFF;
  --ms-option-color-disabled: #D1D5DB;
  --ms-option-color-selected-pointed: #FFFFFF;
  --ms-option-color-selected-disabled: #D1FAE5;
  --ms-option-py: 0.5rem;
  --ms-option-px: 0.75rem;

  --ms-empty-color: #4B5563;
}

.custom_select :deep(.multiselect-placeholder) {
  white-space: nowrap;
}
.custom_input :deep(.is-disabled) {
  opacity: .5;
}

.validation {
  text-align: center;
  color: red;
  margin-left: 10px;
}
</style>
